import { useEffect, useMemo, useRef } from 'react';

import { Stack } from '@mui/material';

import { DisabledTooltipContent } from 'domains/campaigns/components/CustomCampaign/components/DisabledTooltipContent';
import { Send } from 'domains/campaigns/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import { SegmentFilterEngagementFields, SegmentFilterState, SegmentFilterV2 } from 'domains/segments/types';
import useSendById from 'hooks/queries/useSendById';
import useSends from 'hooks/queries/useSends';
import { SendJobStatus } from 'models/enums';

export type Props = {
    activeChannelEngagementFilter?: SegmentFilterV2;
    filter: SegmentFilterState<SegmentFilterEngagementFields>;
    index: number;
    sends: Send[];
    sx?: {
        autocomplete: object;
    };
    updateSegmentFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterEngagementFields>,
        resetPreview?: boolean
    ) => void;
    disabled?: boolean;
    disabledTooltipContent?: React.ReactNode;
};

const SendAutocomplete = ({
    activeChannelEngagementFilter,
    filter,
    index,
    sends,
    sx,
    updateSegmentFilter,
    disabled,
    disabledTooltipContent,
}: Props) => {
    const { autocompleteInputValue, autocompleteValue, categorySpecificFields } = filter;

    const { data: sendsByChannel } = useSends({
        path: activeChannelEngagementFilter?.sendsRef,
        reactQueryOptions: {
            enabled: Boolean(activeChannelEngagementFilter?.sendsRef),
        },
    });

    const sendIdValue = categorySpecificFields?.value;
    const sendIdNumber = typeof sendIdValue === 'number' ? sendIdValue : null;

    const updateSegmentFilterRef = useRef(updateSegmentFilter);
    const filterRef = useRef(filter);
    updateSegmentFilterRef.current = updateSegmentFilter;
    filterRef.current = filter;

    // Check if send exists in available arrays
    const sendExistsInArray = useMemo(() => {
        if (!sendIdNumber) return false;
        const existsInSends = sends?.some((s) => s.id === sendIdNumber);
        const existsInSendsByChannel = sendsByChannel?.some((s) => s.id === sendIdNumber);
        return existsInSends || existsInSendsByChannel;
    }, [sendIdNumber, sends, sendsByChannel]);

    // Fetch send by ID if not in array
    const { data: fetchedSend, isSuccess: fetchedSendSuccess } = useSendById(sendIdNumber ?? 0, {
        enabled: Boolean(sendIdNumber) && !sendExistsInArray,
    });

    // Update autocomplete when fetched send is available.
    // `filter` and `updateSegmentFilter` are accessed via refs so that changing function/object
    // references from parent re-renders do not re-trigger this effect and cause an infinite loop.
    useEffect(() => {
        if (fetchedSend && fetchedSendSuccess && sendIdNumber === fetchedSend.id) {
            const latestFilter = filterRef.current;
            const currentValue = Array.isArray(latestFilter.autocompleteValue)
                ? latestFilter.autocompleteValue[0]
                : latestFilter.autocompleteValue;
            if (
                !currentValue ||
                currentValue.value !== fetchedSend.id ||
                latestFilter.autocompleteInputValue !== fetchedSend.name
            ) {
                updateSegmentFilterRef.current(index, {
                    ...latestFilter,
                    autocompleteValue: {
                        label: fetchedSend.name,
                        value: fetchedSend.id,
                    },
                    autocompleteInputValue: fetchedSend.name,
                });
            }
        }
    }, [fetchedSend, fetchedSendSuccess, sendIdNumber, index]);

    // Include fetched send in dropdown if not already present
    const sendsForDropdown = useMemo(() => {
        // The `sends` prop is the caller-supplied list to surface — e.g. the campaign's own sends,
        // which may be in-flight (negative sentinel ids) or drafts the channel query filters out.
        // Union them with the channel-fetched list rather than letting sendsByChannel replace them,
        // otherwise those caller sends appear and then vanish once sendsByChannel resolves.
        const channelSends = sendsByChannel ?? [];
        const propSends = sends ?? [];
        const channelIds = new Set(channelSends.map((s) => s.id));
        const baseSends = sendsByChannel
            ? [...propSends.filter((s) => !channelIds.has(s.id)), ...channelSends]
            : propSends;

        if (fetchedSend && fetchedSendSuccess && sendIdNumber === fetchedSend.id) {
            const alreadyExists = baseSends.some((s) => s.id === fetchedSend.id);
            if (!alreadyExists) {
                return [...baseSends, fetchedSend];
            }
        }
        return baseSends;
    }, [sendsByChannel, sends, fetchedSend, fetchedSendSuccess, sendIdNumber]);

    const sendDropdownOptions = useMemo(() => {
        const filtered =
            sendsForDropdown?.filter((c) => c.status !== 'FAILED' && c.status !== SendJobStatus.CANCELED) ?? [];
        return filtered.map((c) => ({ label: c.name, value: c.id }));
    }, [sendsForDropdown]);

    const handleInputValueChange = (newInputValue: string) => {
        updateSegmentFilter(index, {
            ...filter,
            autocompleteInputValue: newInputValue,
        });
    };

    const handleValueChange = (value: any) => {
        updateSegmentFilter(index, {
            ...filter,
            autocompleteValue: value,
            categorySpecificFields: {
                ...categorySpecificFields,
                value: value?.value,
                sendInteractionField: {
                    autocompleteInputValue: '',
                    autocompleteValue: null,
                    value: null,
                },
            },
        });
    };

    const sharedProps = {
        inputValue: autocompleteInputValue ?? '',
        isOptionEqualToValue: (option: { value: number }, value: { value: number }) => {
            const optionValue = typeof option.value === 'number' ? option.value : Number(option.value);
            const valueValue = typeof value.value === 'number' ? value.value : Number(value.value);
            return optionValue === valueValue;
        },
        onChange: (_e: any, value: any) => handleValueChange(value),
        onInputChange: (_e: any, newInputValue: string) => handleInputValueChange(newInputValue),
        options: sendDropdownOptions ?? [],
        sx: { width: '33rem', ...sx?.autocomplete },
    };

    // TODO: uncomment when SendAutocomplete supports multi-select
    // if (user can select multiple sends) {
    //     return (
    //         <Stack direction="row" order="3">
    //             <Autocomplete
    //                 data-testid="multi-send-autocomplete"
    //                 label="Sends"
    //                 multiple
    //                 placeholder={
    //                     autocompleteValue && Array.isArray(autocompleteValue) && !!autocompleteValue?.length
    //                         ? ''
    //                         : 'Enter send names'
    //                 }
    //                 renderTags={(value, getTagProps) =>
    //                     value?.map((option, index) => (
    //                         <Chip
    //                             deleteIcon={<Close />}
    //                             label={option.label}
    //                             {...getTagProps({ index })}
    //                             sx={greyChipStyles}
    //                         />
    //                     ))
    //                 }
    //                 type="string"
    //                 value={autocompleteValue ?? []}
    //                 {...sharedProps}
    //             />
    //         </Stack>
    //     );
    // }

    return (
        <Stack direction="row" order="2">
            <DisabledTooltipContent disabled={!!disabled}>
                <span>
                    <Autocomplete
                        data-testid="send-autocomplete"
                        data-test="send-autocomplete"
                        disabled={disabled}
                        label="Send"
                        placeholder="Enter a send name"
                        type="string"
                        value={autocompleteValue ?? null}
                        {...sharedProps}
                    />
                </span>
            </DisabledTooltipContent>
        </Stack>
    );
};

export default SendAutocomplete;
